// The read-only LO card on a loan (D16/D17): who the LO is, their company and NMLS, the AE who owns the
// relationship, tier, Salesforce channel flags and the date of the last touch. No notes or activity
// text. Credit analysts, underwriters and doc & funding see LOs only this way. The LO's name links to
// their Prospecting record for staff who have Prospecting, and only when the loan is linked to that LO.
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ApiError } from "../lib/api";
import {
  channelFlags,
  crmKeys,
  DESK_HREF,
  pendingDealLinks,
  pendingDealsLabel,
  shortDate,
  useLoCard,
  usePendingDealLinks,
  type DealLink,
  type PersonPage,
} from "../lib/crm";
import { useMe } from "../lib/queries";
import { hasProspecting } from "../lib/roles";
import { LoSmartDropButton } from "./SmartDropWidget";

export function LoCardSection({
  loanId,
  dealLinks,
}: {
  loanId: string;
  dealLinks?: readonly DealLink[];
}) {
  const me = useMe();
  const qc = useQueryClient();
  const q = useLoCard(loanId);
  const card = q.error instanceof ApiError ? undefined : q.data?.card;
  const company = q.error instanceof ApiError ? undefined : q.data?.company;
  const fetchedPending = usePendingDealLinks(
    me,
    dealLinks ? null : (card?.person_id ?? null),
  );
  if (q.error instanceof ApiError || !q.data) return null;
  if (!card && !company) return null;
  const flags = card ? channelFlags(card) : [];
  const cachedPerson = card ? qc.getQueryData<PersonPage>(crmKeys.person(card.person_id)) : undefined;
  const pending = dealLinks
    ? pendingDealLinks(dealLinks)
    : fetchedPending.length > 0
      ? fetchedPending.map((x) => x.link)
      : pendingDealLinks(cachedPerson?.deal_links);
  return (
    <section className="mt-5" aria-label="Broker and LO">
      <h3 className="eyebrow mb-2 text-ink-3">Broker / LO</h3>
      <div className="rounded-md border border-subtle bg-sunken px-3 py-2 text-[13px]">
        {card ? (
          <>
            <div className="font-semibold text-ink">
              {hasProspecting(me) ? (
                <Link
                  to={`/prospecting/people/${card.person_id}`}
                  className="focus-ring -my-3 inline-flex min-h-11 items-center rounded-sm text-link hover:underline"
                  title="Open this LO's record in Prospecting"
                >
                  {card.full_name}
                </Link>
              ) : (
                card.full_name
              )}
              {card.nmls ? <span className="font-normal text-ink-2"> · NMLS {card.nmls}</span> : null}
            </div>
            <div className="text-ink-2">{card.company_name ?? company?.company_name ?? "No company"}</div>
            <div className="text-[12px] text-ink-3">
              {card.ae_name ? `AE ${card.ae_name}` : "No AE relationship"}
              {card.tier ? ` · ${card.tier}` : ""} · last touch {shortDate(card.last_touch_at)}
            </div>
            {flags.length ? <div className="text-[12px] font-semibold text-warning">{flags.join(" · ")} (Salesforce)</div> : null}
            <div className="mt-2">
              <LoSmartDropButton
                personId={card.person_id}
                loName={card.full_name}
                companyId={card.company_id}
                companyName={card.company_name ?? company?.company_name}
                compact
              />
            </div>
            {pending.length > 0 ? (
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 border-t border-subtle pt-1.5 text-[12px]">
                <span className="font-semibold text-ink">{pendingDealsLabel(pending.length)}</span>
                {me.user.desk_available ? (
                  <Link to={DESK_HREF} reloadDocument className="focus-ring font-semibold text-link hover:underline">
                    Open the Desk
                  </Link>
                ) : (
                  <span className="text-ink-3">Desk unavailable</span>
                )}
              </div>
            ) : null}
          </>
        ) : (
          <div className="font-semibold text-ink">
            {company?.company_name}
            {company?.nmls ? <span className="font-normal text-ink-2"> · NMLS {company.nmls}</span> : null}
          </div>
        )}
      </div>
    </section>
  );
}
